import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { ActivatedRoute } from '@angular/router';

// import { MbscEventcalendarOptions } from '@mobiscroll/angular'; TURN ON TO VALIDATE OPTIONS AS NEEDED. Too large to justify keeping all the time
import { ActivityService } from 'src/app/core/services/activity.service';
import { MatDialog } from '@angular/material/dialog';
import { EventFormModalComponent } from 'src/app/manage/activities/event-form-modal-component/event-form-modal.component';
import {
  EventColors,
  EventType,
} from '../../../../../../../../core/enums/event-type';
import { AlertService } from 'src/app/core/services/alert.service';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogValues,
} from 'src/app/shared/modals/confirmation-dialog/confirmation-dialog.component';
import { ManageService } from 'src/app/core/services/manage.service';
import {
  DeleteRecurrenceDialogComponent,
  EventDeleteDialogValues,
} from 'src/app/shared/modals/delete-recurrence-modal/delete-recurrence-dialog.component';
import { RecurrenceService } from 'src/app/core/services/recurrence.service';
import { EventTag } from '@models/eventTag';

@Component({
  selector: 'app-event-page-agenda',
  templateUrl: './event-page-agenda.component.html',
  styleUrls: ['./event-page-agenda.component.scss'],
})

// Event calendar view. Includes controls for changing the active month, adding events, deleting events
export class EventPageAgendaComponent implements OnInit, OnDestroy {
  destroy$: Subject<boolean> = new Subject();
  public events: BehaviorSubject<any[]> = new BehaviorSubject([]);
  @Output() changeMonth = new EventEmitter();
  @Output() save = new EventEmitter();
  @Output() refreshNeeded = new EventEmitter<void>();
  @Input() months: Array<Date>;
  @Input() activeMonth: Date;
  @Input() printCalendarRestrictions: {
    monthData;
    maxEventRows;
    maxChars: { small: number; regular: number } | number;
    eventRowLimits;
  };
  @Input() timezone;
  eventTypes = EventType;
  options = {
    view: {
      // calendar: { type: 'month', labels: true },
      agenda: { type: 'month', size: 1 },
    },
    theme: 'goodman',
    dateFormatLong: 'DDDD, MMM D, YYYY',
    noEventsText: 'No scheduled events',
    eventOrder: this.sortEvents,
  };
  selectedDate = new Date();
  monthIndex = 12;
  public loading = true;
  private siteId: number;

  constructor(
    public activityService: ActivityService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private alertService: AlertService,
    private manageService: ManageService,
    private recurrenceService: RecurrenceService,
  ) {}

  get currentSiteId(): number {
    return this.siteId;
  }

  /**
   * We sort our events correctly on the BE. We need to override mobiscroll's default behavior of
   * sorting by all day events, then time, then alphabetically by title.
   **/
  sortEvents() {
    return 1;
  }

  ngOnInit(): void {
    this.siteId = this.route.snapshot.params['siteId'];
    this.activityService.activities
      .pipe(
        map((activities) => {
          let mappedToCalEvents = activities.map((activity, index) => {
            // Properly convert UTC times to site's timezone
            const eventStartDate = parseISO(activity.eventStart.toString());
            const eventEndDate = parseISO(activity.eventEnd.toString());

            // Use utcToZonedTime to correctly convert from UTC to site's timezone
            const startInSiteTimezone = utcToZonedTime(
              eventStartDate,
              this.timezone,
            );
            const endInSiteTimezone = utcToZonedTime(
              eventEndDate,
              this.timezone,
            );

            const location = activity.location
              ? activity.location
              : activity.room?.shortName &&
                activity.room?.shortName !== ' - ' &&
                activity.room?.shortName !== '-'
              ? activity.room?.shortName
              : '';

            const formattedActivity = {
              start: startInSiteTimezone,
              end: endInSiteTimezone,
              title: activity.title,
              allDay: activity.allDay,
              icon: (activity?.eventTags[0] as EventTag)?.icon,
              eventTag: activity?.eventTags[0],
              color: EventColors[activity.eventType],
              eventType: activity.eventType,
              location: location,
              room: {
                long: activity.room?.name,
                short: location,
              },
              description: activity.content,
              showOnPrintCalendar: activity.showOnPrintCalendar,
              showOnDigitalSignage: activity.showOnDigitalSignage,
              src: activity,
              recurrence: activity?.recurrence
                ? this.recurrenceService.prettyPrintRecurrenceRecipe({
                    ...activity.recurrence,
                  })
                : null,
            };

            return formattedActivity;
          });
          return mappedToCalEvents;
        }),
        // Simplified distinctUntilChanged - only check length and a simple hash of IDs
        distinctUntilChanged((prev, curr) => {
          // If lengths are different, definitely changed
          if (prev.length !== curr.length) {
            return false;
          }

          // If both are empty, they're the same
          if (prev.length === 0 && curr.length === 0) {
            return true;
          }

          // Create a simple hash of IDs to detect changes
          const prevIds = prev
            .map((event) => event.src.id)
            .sort()
            .join(',');
          const currIds = curr
            .map((event) => event.src.id)
            .sort()
            .join(',');

          // If the ID sets are different, the arrays have changed
          return prevIds === currIds;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((activities) => {
        this.events.next(activities);
        this.loading = false;
      });

    // Also subscribe to loading state
    this.activityService.isGettingEvents
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoading) => {
        this.loading = isLoading;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  jumpToThisMonth() {
    this.changeMonth.emit(12);
  }

  handlePageLoading(event): void {
    this.loading = true;
    const activeMonth = this.activeMonth;
    if (!activeMonth) {
      this.alertService.error('Sorry! That date is out of range.');
    }

    // Use event.month directly since it's already in the correct format
    const chosenMonth = new Date(event.month);
    const month = chosenMonth.getMonth(); // getMonth() returns 0-based months (0-11)
    const year = chosenMonth.getFullYear();
    if (
      activeMonth.getMonth() === month &&
      activeMonth.getFullYear() === year
    ) {
      this.loading = false;
      return;
    }

    const monthIndex = this.months.findIndex((value) => {
      return value.getMonth() === month && value.getFullYear() === year;
    });

    if (monthIndex < 0) {
      this.alertService.error('An error has occurred.');
      return;
    }

    this.changeMonth.emit(monthIndex);
  }

  // Add a method to force a complete reload of the events
  public forceReload(): void {
    // Set loading state immediately
    this.loading = true;

    // Clear the current events to ensure a clean state
    this.events.next([]);

    // Directly refresh activities - the subscription will handle the UI update
    this.activityService
      .refreshActivities(this.activeMonth, true)
      .catch((error) => {
        console.error('Error during force reload:', error);
        this.loading = false;
        this.alertService.error('Failed to refresh events. Please try again.');
      });
  }

  public showEditDialog(event): void {
    const dialogRef = this.dialog.open(EventFormModalComponent, {
      width: '80vw',
      height: '660px',
      data: {
        event: event.original.src,
        selectedMonth: this.activeMonth,
        months: this.months,
        editing: true,
      },
    });

    // Check if a refresh is needed when the dialog is closed
    dialogRef.afterClosed().subscribe((refreshNeeded) => {
      if (refreshNeeded) {
        // Emit to parent component to handle refresh properly
        this.refreshNeeded.emit();
      }
    });
  }

  public addEvent(): void {
    const dialogRef = this.dialog.open(EventFormModalComponent, {
      width: '80vw',
      height: '660px',
      data: {
        selectedMonth: this.activeMonth,
        months: this.months,
      },
    });

    // Check if a refresh is needed when the dialog is closed
    dialogRef.afterClosed().subscribe((refreshNeeded) => {
      if (refreshNeeded) {
        // Use the force reload method to ensure UI updates
        this.forceReload();
      }
    });
  }

  public deleteActivity(event): void {
    const activity = event.original.src;

    if (!activity.recurrence) {
      const confirmData: ConfirmationDialogValues = {
        title: `Delete "${activity.title}"?`,
        message: 'This action can not be undone.',
        yesText: 'Delete',
        onConfirm: async () => {
          await this.manageService.deleteAnnouncement(activity);
        },
        onError: (error) => {
          this.alertService.error(
            `Error deleting "${activity.title}". Please try again.`,
          );
        },
      };
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: confirmData,
      });

      dialogRef.afterClosed().subscribe(async (result) => {
        if (result) {
          // Use the force reload method to ensure UI updates
          this.forceReload();
          this.alertService.success(`"${activity.title}" deleted.`);
        }
      });
    } else {
      const confirmData: EventDeleteDialogValues = {
        title: `Delete "${activity.title}"?`,
        message: 'This is a recurring event.',
        yesText: 'Delete',
        onError: (error) => {
          this.alertService.error(
            `Error deleting "${activity.title}". Please try again.`,
          );
        },
        onSuccess: async () => {
          // Use the force reload method to ensure UI updates
          this.forceReload();
        },
        activity,
      };
      const dialogRef = this.dialog.open(DeleteRecurrenceDialogComponent, {
        data: confirmData,
      });
    }
  }
}
